import { Asset, DebtPosition, Nullable } from '../../interfaces';
import { Decimal, ONE } from '../../datastructures';
import { walletAddressAtom } from '../wallet';
import nostraTokenBalancesAtom, { NostraTokenBalanceMap } from './nostraTokenBalanceAtom';
import assetsAtom from './assetsAtom';
import assetTokenRatesAtom from './assetTokenRatesAtom';
import { activeAccountIdAtom } from './activeAccountIdAtom';
import { createAsyncAtom } from '../../lib/state';

type AssetAddressPositionMap = { [walletAddressAccountIdAssetAddress: string]: DebtPosition };

const fetchData = (
  walletAddress: string,
  accountId: number,
  asset: Asset,
  collateralFactor: Decimal,
  borrowRate: Decimal,
  tokenRate: Decimal,
  nostraTokenBalances: NostraTokenBalanceMap,
): Nullable<DebtPosition> => {
  const balance = nostraTokenBalances[`${walletAddress}-${accountId}-${asset.debtTokenAddress}`];

  // still loading the nostra token balance
  if (!balance) {
    return null;
  }

  const balanceUsd = balance.mul(tokenRate);

  return {
    asset,
    balance,
    balanceUsd,
    apy: borrowRate,
    collateralFactor,
  };
};

const debtPositionsAtom = createAsyncAtom(async get => {
  const assets = await get(assetsAtom);
  const assetTokenRates = await get(assetTokenRatesAtom);
  const nostraTokenBalances = await get(nostraTokenBalancesAtom);
  const walletAddress = await get(walletAddressAtom);
  const activeAccountId = await get(activeAccountIdAtom);

  if (!assets || !walletAddress) {
    return null;
  }

  const debtPositions: AssetAddressPositionMap = {};
  assets.forEach(asset => {
    if (!asset.borrowApr || !asset.collateralFactor) {
      return null;
    }

    const tokenRate = assetTokenRates.rate[asset.address] ?? ONE;

    const debtPosition = fetchData(
      walletAddress,
      activeAccountId,
      asset,
      asset.collateralFactor,
      asset.borrowApr,
      tokenRate,
      nostraTokenBalances,
    );

    if (debtPosition && debtPosition.balance.gt(0)) {
      debtPositions[`${walletAddress}-${activeAccountId}-${asset.address}`] = debtPosition;
    }
  });

  return Object.values(debtPositions).sort((a, b) =>
    b.balanceUsd.gt(a.balanceUsd) ? 1 : b.balanceUsd.lt(a.balanceUsd) ? -1 : 0,
  );
});
export default debtPositionsAtom;
